import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { CheckCircle2, Home, MapPin, Search, Trash2 } from "lucide-react";
import { api, type HomeCandidate } from "../lib/api";
import type { HomeLocation } from "../lib/ai-chat";

type Fields = { address: string; postalCode: string; city: string; province: string };
const emptyFields: Fields = { address: "", postalCode: "", city: "", province: "" };

function fieldsFrom(home?: HomeLocation): Fields {
  return home ? { address: home.address, postalCode: home.postalCode, city: home.city, province: home.province } : emptyFields;
}

export function SettingsPanel({ home, onHomeChange }: { home?: HomeLocation; onHomeChange: (home?: HomeLocation) => void }) {
  const [fields, setFields] = useState<Fields>(() => fieldsFrom(home));
  const [selected, setSelected] = useState<HomeCandidate | undefined>(home);
  const [suggestions, setSuggestions] = useState<HomeCandidate[]>([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string>();
  const request = useRef<AbortController | undefined>(undefined);
  const query = useMemo(() => [fields.address, fields.postalCode, fields.city, fields.province].filter(Boolean).join(", "), [fields]);

  useEffect(() => () => request.current?.abort(), []);
  useEffect(() => { setFields(fieldsFrom(home)); setSelected(home); }, [home]);
  useEffect(() => {
    if (selected || fields.address.trim().length < 4 || (!fields.city.trim() && fields.postalCode.trim().length < 5)) {
      setSuggestions([]); setSearching(false); return;
    }
    setSearching(true);
    const timer = window.setTimeout(() => {
      request.current?.abort();
      const controller = new AbortController(); request.current = controller;
      api.homeGeocode(query, controller.signal).then(({ places }) => setSuggestions(places)).catch((cause) => {
        if (!(cause instanceof DOMException && cause.name === "AbortError")) setMessage(cause instanceof Error ? cause.message : "Ricerca indirizzo non disponibile");
      }).finally(() => { if (!controller.signal.aborted) setSearching(false); });
    }, 550);
    return () => window.clearTimeout(timer);
  }, [fields.address, fields.city, fields.postalCode, fields.province, query, selected]);

  function update(name: keyof Fields, value: string) {
    setFields((current) => ({ ...current, [name]: value }));
    setSelected(undefined); setMessage(undefined);
  }

  function choose(candidate: HomeCandidate) {
    setSelected(candidate); setFields(fieldsFrom(candidate)); setSuggestions([]); setMessage(undefined);
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!selected || saving) { setMessage("Seleziona un indirizzo riconosciuto dai suggerimenti prima di salvarlo."); return; }
    setSaving(true); setMessage(undefined);
    try {
      const result = await api.saveSettingsHome(selected);
      setSelected(result.home); setFields(fieldsFrom(result.home)); onHomeChange(result.home);
      setMessage("Indirizzo verificato e salvato nel tuo profilo.");
    } catch (cause) { setMessage(cause instanceof Error ? cause.message : "Impossibile salvare l’indirizzo"); }
    finally { setSaving(false); }
  }

  async function remove() {
    setSaving(true); setMessage(undefined);
    try { await api.removeSettingsHome(); setFields(emptyFields); setSelected(undefined); onHomeChange(undefined); setMessage("Indirizzo rimosso."); }
    catch (cause) { setMessage(cause instanceof Error ? cause.message : "Impossibile rimuovere l’indirizzo"); }
    finally { setSaving(false); }
  }

  return <div className="panel-content settings-panel">
    <div className="section-heading"><span className="eyebrow">Preferenze personali</span><h1>Impostazioni</h1><p>Configura i dati usati per calcolare distanze e tempi di viaggio.</p></div>
    <section className="settings-card">
      <div className="settings-card-heading"><span><Home /> Casa</span>{home && <button type="button" onClick={() => void remove()} disabled={saving} aria-label="Rimuovi indirizzo"><Trash2 /></button>}</div>
      <h2>Indirizzo di partenza</h2>
      <p>Compila i campi e scegli un risultato verificato. Non è possibile salvare testo libero.</p>
      <form onSubmit={save}>
        <label>Indirizzo<input value={fields.address} onChange={(event) => update("address", event.target.value)} placeholder="Via e numero civico" autoComplete="street-address" /></label>
        <div className="settings-address-grid">
          <label>CAP<input value={fields.postalCode} onChange={(event) => update("postalCode", event.target.value.replace(/\D/g, "").slice(0, 5))} inputMode="numeric" autoComplete="postal-code" placeholder="00000" /></label>
          <label>Città<input value={fields.city} onChange={(event) => update("city", event.target.value)} autoComplete="address-level2" placeholder="Comune" /></label>
        </div>
        <label>Provincia<input value={fields.province} onChange={(event) => update("province", event.target.value)} autoComplete="address-level1" placeholder="Provincia" /></label>
        <div className="address-results" aria-live="polite">
          {searching && <div className="address-searching"><span className="spinner" />Cerco indirizzi reali…</div>}
          {!searching && suggestions.map((candidate) => <button type="button" key={`${candidate.latitude}-${candidate.longitude}`} onClick={() => choose(candidate)}><MapPin /><span><strong>{candidate.address}</strong><small>{candidate.postalCode} {candidate.city} · {candidate.province}</small></span></button>)}
        </div>
        {selected && <div className="address-verified"><CheckCircle2 /><span><strong>Indirizzo riconosciuto</strong><small>{selected.label}</small></span></div>}
        <button className="settings-save" disabled={!selected || saving}>{saving ? "Salvo…" : "Salva indirizzo"}</button>
      </form>
      {message && <p className="settings-message">{message}</p>}
      <small className="geocoder-credit"><Search />Ricerca basata su OpenStreetMap tramite Photon.</small>
    </section>
  </div>;
}
